import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  IconButton
} from '@mui/material';
import {
  Store as StoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { fetchOwnerStores, setSelectedStore, clearStoreData } from '../slices/storeSlice';
import { setCurrentStore } from '../utils/storeContext';
import EditStoreDialog from './EditStoreDialog';

const StoreSelectorHeader = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { ownerStores, selectedStore, status } = useSelector((state) => state.store);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (user?.role === 'Owner') {
      dispatch(fetchOwnerStores());
    }
  }, [dispatch, user]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStoreSelect = (store) => {
    // Clear existing data first
    dispatch(clearStoreData());
    
    // Set the new store
    dispatch(setSelectedStore(store));
    setCurrentStore(store);
    handleClose();
  };

  const handleEditStore = (store, event) => {
    // Prevent the menu item click event from firing
    event.stopPropagation();
    
    setOpenEditDialog(true);
    handleClose();
  };

  const handleEditSuccess = () => {
    // Refresh the stores list after successful edit
    dispatch(fetchOwnerStores());
  };

  const handleCreateStore = () => {
    handleClose();
    // Navigate to stores page to create new store
    window.location.href = '/stores';
  };

  // Only show for owners
  if (user?.role !== 'Owner') {
    return null;
  }

  // Don't show if loading or no stores
  if (status === 'loading' || !ownerStores || ownerStores.length === 0) {
    return null;
  }

  return (
    <Box>
      <Button
        onClick={handleClick}
        variant="outlined"
        size="small"
        startIcon={<StoreIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          minWidth: 'auto',
          px: 2,
          py: 1,
          fontSize: '0.875rem',
          textTransform: 'none',
          borderColor: 'divider',
          color: 'text.primary',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        {selectedStore ? selectedStore.name : 'Select Store'}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            maxHeight: 300,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            My Stores
          </Typography>
        </Box>
        
        <Divider />
        
        {ownerStores.map((store) => (
          <MenuItem
            key={store.storeId}
            onClick={() => handleStoreSelect(store)}
            selected={selectedStore?.storeId === store.storeId}
            sx={{
              py: 1,
              px: 2,
            }}
          >
            <ListItemIcon>
              <StoreIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={store.name}
              secondary={store.storeId}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: selectedStore?.storeId === store.storeId ? 'bold' : 'normal',
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                color: 'text.secondary',
              }}
            />
            {selectedStore?.storeId === store.storeId && (
              <IconButton
                size="small"
                onClick={(e) => handleEditStore(store, e)}
                sx={{ 
                  ml: 1,
                  color: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.light',
                    color: 'white'
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </MenuItem>
        ))}
        
        <Divider />
        
        <MenuItem onClick={handleCreateStore} sx={{ py: 1, px: 2 }}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Create New Store"
            primaryTypographyProps={{
              variant: 'body2',
            }}
          />
        </MenuItem>
      </Menu>

      {/* Edit Store Dialog */}
      <EditStoreDialog
        store={selectedStore}
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        onSuccess={handleEditSuccess}
      />
    </Box>
  );
};

export default StoreSelectorHeader; 